import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useSearchParams } from 'react-router-dom';
import MemoryGameBasic from './MemoryGameBasic';
import '../../styles/MemoryGame.css';

interface Card {
  id: number;
  animal: string;
  emoji: string;
  isFlipped: boolean;
  isMatched: boolean;
}

interface GameStats {
  moves: number;
  matches: number;
  timeElapsed: number;
  gameStarted: boolean;
  gameCompleted: boolean;
}

const ANIMALS = [
  { name: 'cat', emoji: '🐱' },
  { name: 'dog', emoji: '🐶' },
  { name: 'rabbit', emoji: '🐰' },
  { name: 'bear', emoji: '🐻' },
  { name: 'lion', emoji: '🦁' },
  { name: 'tiger', emoji: '🐯' },
  { name: 'elephant', emoji: '🐘' },
  { name: 'monkey', emoji: '🐵' },
  { name: 'penguin', emoji: '🐧' },
  { name: 'dolphin', emoji: '🐬' },
];

const DIFFICULTY_LEVELS = {
  easy: { pairs: 6, gridSize: 4, name: 'Easy' },
  medium: { pairs: 8, gridSize: 4, name: 'Medium' },
  hard: { pairs: 12, gridSize: 6, name: 'Hard' }
} as const;

const VersionSwitcher: React.FC<{ currentVersion: string }> = ({ currentVersion }) => {
  return (
    <div className="version-switcher">
      <h3>Compare Versions:</h3>
      <div className="version-buttons">
        <Link
          to="?version=1.0"
          className={`version-btn ${currentVersion === '1.0' ? 'active' : ''} v10`}
        >
          Synoppy 1.0 - Basic
        </Link>
        <Link
          to="?version=1.5"
          className={`version-btn ${currentVersion === '1.5' ? 'active' : ''} v15`}
        >
          Synoppy 1.5 - Advanced
        </Link>
      </div>
    </div>
  );
};

const MemoryGameAdvanced: React.FC = () => {
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [stats, setStats] = useState<GameStats>({
    moves: 0,
    matches: 0,
    timeElapsed: 0,
    gameStarted: false,
    gameCompleted: false
  });
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');
  const [showCelebration, setShowCelebration] = useState(false);
  const [gameMode, setGameMode] = useState<'single' | 'multiplayer'>('single');
  const [currentPlayer, setCurrentPlayer] = useState<1 | 2>(1);
  const [playerScores, setPlayerScores] = useState<{ player1: number; player2: number }>({ player1: 0, player2: 0 });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [aiDifficulty, setAiDifficulty] = useState(0.5);
  const [performance, setPerformance] = useState({ accuracy: 0, avgTime: 0, streak: 0 });
  const [onlineUsers, setOnlineUsers] = useState(Math.floor(Math.random() * 5) + 3);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  const playSound = useCallback(async (type: 'flip' | 'match' | 'win' | 'mismatch' | 'achievement') => {
    if (!soundEnabled) return;

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const createTone = (frequency: number, startTime: number, duration: number, volume: number = 0.3) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(frequency, startTime);
        gainNode.gain.setValueAtTime(volume, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
        return oscillator;
      };

      const now = audioContext.currentTime;

      switch (type) {
        case 'flip':
          createTone(800, now, 0.1, 0.2);
          break;
        case 'match':
          createTone(523, now, 0.3, 0.25);
          createTone(659, now + 0.05, 0.3, 0.25);
          createTone(784, now + 0.1, 0.3, 0.25);
          break;
        case 'win':
          createTone(523, now, 0.2, 0.3);
          createTone(659, now + 0.15, 0.2, 0.3);
          createTone(784, now + 0.3, 0.2, 0.3);
          createTone(1047, now + 0.45, 0.3, 0.3);
          break;
        case 'mismatch':
          createTone(300, now, 0.15, 0.2);
          createTone(200, now + 0.1, 0.2, 0.2);
          break;
        case 'achievement':
          createTone(1047, now, 0.15, 0.35);
          createTone(1319, now + 0.1, 0.15, 0.35);
          createTone(1568, now + 0.2, 0.2, 0.35);
          break;
      }

      setTimeout(() => {
        audioContext.close();
      }, 1000);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  }, [soundEnabled]);

  // Simulate online users changing
  useEffect(() => {
    const interval = setInterval(() => {
      setOnlineUsers(prev => Math.max(1, prev + Math.floor(Math.random() * 3) - 1));
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // AI Difficulty Adjustment
  useEffect(() => {
    const accuracy = stats.moves > 0 ? (stats.matches * 2) / stats.moves : 0;
    if (accuracy > 0.8 && aiDifficulty < 0.9) {
      setAiDifficulty(prev => Math.min(0.9, prev + 0.1));
    } else if (accuracy < 0.4 && aiDifficulty > 0.2) {
      setAiDifficulty(prev => Math.max(0.2, prev - 0.1));
    }
  }, [stats.matches, stats.moves, aiDifficulty]);

  // Performance tracking
  useEffect(() => {
    if (stats.moves > 0) {
      const accuracy = (stats.matches * 2) / stats.moves;
      const avgTime = stats.timeElapsed / stats.moves;
      setPerformance({
        accuracy: Math.round(accuracy * 100),
        avgTime: Math.round(avgTime * 10) / 10,
        streak: streak
      });
    }
  }, [stats.moves, stats.matches, stats.timeElapsed, streak]);

  const initializeGame = useCallback(() => {
    const { pairs } = DIFFICULTY_LEVELS[difficulty];
    const selectedAnimals = ANIMALS.slice(0, pairs);
    const cardPairs = [...selectedAnimals, ...selectedAnimals];

    const shuffledCards: Card[] = cardPairs
      .map((animal, index) => ({
        id: index, // ID is index within this shuffled deck; remains stable for this game
        animal: animal.name,
        emoji: animal.emoji,
        isFlipped: false,
        isMatched: false
      }))
      .sort(() => Math.random() - 0.5);

    setCards(shuffledCards);
    setFlippedCards([]);
    setStats({
      moves: 0,
      matches: 0,
      timeElapsed: 0,
      gameStarted: false,
      gameCompleted: false
    });
    setShowCelebration(false);
    setStreak(0);
  }, [difficulty]);

  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (stats.gameStarted && !stats.gameCompleted) {
      timer = setInterval(() => {
        setStats(prev => ({ ...prev, timeElapsed: prev.timeElapsed + 1 }));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [stats.gameStarted, stats.gameCompleted]);

  const handleCardClick = (cardId: number) => {
    // prevent flipping more than 2 or re-flipping matched/flipped
    const clicked = cards.find(c => c.id === cardId);
    if (!clicked || flippedCards.length === 2 || clicked.isFlipped || clicked.isMatched) {
      return;
    }

    if (!stats.gameStarted) {
      setStats(prev => ({ ...prev, gameStarted: true }));
    }

    playSound('flip');

    // Flip locally first (no race with setState callback)
    const newCards = cards.map(card =>
      card.id === cardId ? { ...card, isFlipped: true } : card
    );
    setCards(newCards);

    const newFlipped = [...flippedCards, cardId];
    setFlippedCards(newFlipped);

    // Only proceed when two cards are flipped
    if (newFlipped.length !== 2) return;

    setStats(prev => ({ ...prev, moves: prev.moves + 1 }));

    const [firstId, secondId] = newFlipped;

    // IDs are guaranteed to exist within newCards during this move
    const firstCard = newCards.find(c => c.id === firstId)!;
    const secondCard = newCards.find(c => c.id === secondId)!;

    if (firstCard.animal === secondCard.animal) {
      playSound('match');

      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) {
          setBestStreak(newStreak);
          if (newStreak >= 5) playSound('achievement');
        }
        return newStreak;
      });

      if (gameMode === 'multiplayer') {
        setPlayerScores(prev => {
          const key = currentPlayer === 1 ? 'player1' : 'player2';
          return { ...prev, [key]: prev[key] + 1 };
        });
      }

      setTimeout(() => {
        setCards(prev =>
          prev.map(card =>
            card.id === firstId || card.id === secondId
              ? { ...card, isMatched: true }
              : card
          )
        );
        setFlippedCards([]);
        setStats(prev => {
          const newMatches = prev.matches + 1;
          const totalPairs = DIFFICULTY_LEVELS[difficulty].pairs;
          if (newMatches === totalPairs) {
            playSound('win');
            setShowCelebration(true);
            return { ...prev, matches: newMatches, gameCompleted: true };
          }
          return { ...prev, matches: newMatches };
        });
      }, 500);
    } else {
      playSound('mismatch');
      setStreak(0);

      if (gameMode === 'multiplayer') {
        setCurrentPlayer(prev => (prev === 1 ? 2 : 1));
      }

      setTimeout(() => {
        setCards(prev =>
          prev.map(card =>
            card.id === firstId || card.id === secondId
              ? { ...card, isFlipped: false }
              : card
          )
        );
        setFlippedCards([]);
      }, 1000);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getGridClass = () => {
    switch (difficulty) {
      case 'easy': return 'grid-easy';
      case 'medium': return 'grid-medium';
      case 'hard': return 'grid-hard';
      default: return 'grid-easy';
    }
  };

  return (
    <div className="memory-game">
      <div className="game-header">
        <Link to="/" className="back-button">← Back to Home</Link>
        <h1>🎮 Animal Memory Game</h1>
        <div className="game-info">
          <span className="version-badge synoppy-15">Synoppy 1.5</span>
        </div>
      </div>

      <div className="advanced-controls">
        <div className="control-row">
          <div className="game-mode-selector">
            <label>Game Mode:</label>
            <select
              value={gameMode}
              onChange={(e) => setGameMode(e.target.value as 'single' | 'multiplayer')}
              className="mode-select"
            >
              <option value="single">Single Player</option>
              <option value="multiplayer">Multiplayer</option>
            </select>
          </div>

          <div className="difficulty-selector">
            <label>Difficulty:</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
              className="difficulty-select"
            >
              {Object.entries(DIFFICULTY_LEVELS).map(([key, level]) => (
                <option key={key} value={key}>{level.name} ({level.pairs} pairs)</option>
              ))}
            </select>
          </div>

          <div className="sound-controls">
            <label>Sound:</label>
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`sound-toggle ${soundEnabled ? 'on' : 'off'}`}
            >
              {soundEnabled ? '🔊' : '🔇'}
            </button>
          </div>

          <button onClick={initializeGame} className="new-game-btn">
            🔄 New Game
          </button>
        </div>

        <div className="real-time-features">
          <div className="online-indicator">
            <div className="pulse-dot"></div>
            <span>{onlineUsers} players online</span>
          </div>

          <div className="ai-status">
            <span>🤖 AI Difficulty: {Math.round(aiDifficulty * 100)}%</span>
          </div>

          {streak > 0 && (
            <div className="streak-indicator">
              🔥 Streak: {streak} {streak >= 5 && '🏆'}
            </div>
          )}
        </div>
      </div>

      <div className="game-dashboard">
        <div className="primary-stats">
          <div className="stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-info">
              <span className="stat-value">{formatTime(stats.timeElapsed)}</span>
              <span className="stat-label">Time</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-info">
              <span className="stat-value">{stats.moves}</span>
              <span className="stat-label">Moves</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <span className="stat-value">{stats.matches}/{DIFFICULTY_LEVELS[difficulty].pairs}</span>
              <span className="stat-label">Matches</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🏆</div>
            <div className="stat-info">
              <span className="stat-value">{bestStreak}</span>
              <span className="stat-label">Best Streak</span>
            </div>
          </div>
        </div>

        <div className="performance-analytics">
          <div className="analytics-header">
            <h3>📊 Performance Analytics</h3>
          </div>
          <div className="analytics-grid">
            <div className="metric">
              <span className="metric-label">Accuracy:</span>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${performance.accuracy}%` }}
                ></div>
              </div>
              <span className="metric-value">{performance.accuracy}%</span>
            </div>
            <div className="metric">
              <span className="metric-label">Avg Time/Move:</span>
              <span className="metric-value">{performance.avgTime}s</span>
            </div>
          </div>
        </div>

        {gameMode === 'multiplayer' && (
          <div className="multiplayer-section">
            <div className="player-scores">
              <div className={`player-card ${currentPlayer === 1 ? 'active' : ''}`}>
                <div className="player-info">
                  <span className="player-name">Player 1</span>
                  <span className="player-score">{playerScores.player1}</span>
                </div>
              </div>
              <div className="vs-indicator">VS</div>
              <div className={`player-card ${currentPlayer === 2 ? 'active' : ''}`}>
                <div className="player-info">
                  <span className="player-name">Player 2</span>
                  <span className="player-score">{playerScores.player2}</span>
                </div>
              </div>
            </div>
            <div className="turn-indicator">
              {!stats.gameCompleted && (
                <span>🎯 Player {currentPlayer}'s Turn</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={`cards-grid ${getGridClass()}`}>
        {cards.map((card) => (
          <div
            key={card.id}
            className={`card ${card.isFlipped || card.isMatched ? 'flipped' : ''} ${card.isMatched ? 'matched' : ''}`}
            onClick={() => handleCardClick(card.id)}
          >
            <div className="card-inner">
              <div className="card-front">
                <div className="card-pattern">🎴</div>
              </div>
              <div className="card-back">
                <div className="animal-emoji">{card.emoji}</div>
                <div className="animal-name">{card.animal}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showCelebration && (
        <div className="celebration-overlay">
          <div className="celebration-content">
            <div className="celebration-emoji">🎉</div>
            <h2>Congratulations!</h2>
            <p>You completed the game in {stats.moves} moves and {formatTime(stats.timeElapsed)}!</p>
            <div className="celebration-stars">⭐ ⭐ ⭐</div>
            <button onClick={() => setShowCelebration(false)} className="close-celebration">
              Play Again
            </button>
          </div>
        </div>
      )}

      <div className="game-navigation">
        <Link to="/memory-game/about" className="nav-link">About This Game</Link>
      </div>
    </div>
  );
};

const AboutPage: React.FC = () => {
  return (
    <div className="about-page">
      <div className="about-header">
        <Link to="/memory-game" className="back-button">← Back to Game</Link>
        <h1>About Animal Memory Game</h1>
      </div>

      <div className="about-content">
        <div className="prompt-section">
          <h2>🤖 AI Prompt Used</h2>
          <div className="prompt-box">
            "Create an animal memory card game with sound effects, smooth animations, and multiple difficulty levels"
          </div>
        </div>

        <div className="features-section">
          <h2>✨ Synoppy 1.5 Features</h2>
          <ul className="features-list">
            <li>🎵 Dynamic sound effects for different actions (flip, match, win, mismatch)</li>
            <li>🎨 Beautiful CSS animations and transitions</li>
            <li>⚙️ Multiple difficulty levels (Easy, Medium, Hard)</li>
            <li>📊 Real-time game statistics tracking</li>
            <li>🏆 Celebration animation on game completion</li>
            <li>📱 Responsive design for all screen sizes</li>
            <li>🎯 Smooth card flip animations</li>
            <li>⏱️ Live timer functionality</li>
          </ul>
        </div>

        <div className="how-to-play">
          <h2>🎮 How to Play</h2>
          <ol>
            <li>Choose your difficulty level</li>
            <li>Click on cards to reveal the animals</li>
            <li>Match two identical animals to score</li>
            <li>Complete all pairs to win!</li>
            <li>Try to finish with the fewest moves and fastest time</li>
          </ol>
        </div>

        <div className="tech-details">
          <h2>🔧 Technical Implementation</h2>
          <ul>
            <li>React with TypeScript for type safety</li>
            <li>Web Audio API for procedural sound generation</li>
            <li>CSS Grid and Flexbox for responsive layout</li>
            <li>React Router for navigation</li>
            <li>Local state management with hooks</li>
            <li>Custom animations with CSS transforms</li>
          </ul>
        </div>

        <div className="navigation-links">
          <Link to="/" className="nav-button">🏠 Home</Link>
          <Link to="/memory-game" className="nav-button primary">🎮 Play Game</Link>
        </div>
      </div>
    </div>
  );
};

const MemoryGame: React.FC = () => {
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version') || '1.5';

  if (version === '1.0') {
    return (
      <div className="memory-game-container">
        <Routes>
          <Route path="/" element={
            <div>
              <VersionSwitcher currentVersion="1.0" />
              <MemoryGameBasic />
            </div>
          } />
          <Route path="/about" element={<AboutPage />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="memory-game-container">
      <Routes>
        <Route path="/" element={
          <div>
            <VersionSwitcher currentVersion="1.5" />
            <MemoryGameAdvanced />
          </div>
        } />
        <Route path="/about" element={<AboutPage />} />
      </Routes>
    </div>
  );
};

export default MemoryGame;
